﻿<script setup lang="ts">
    import { computed, onMounted, ref, watch, toRef } from 'vue';
    import { getLibrariesAuditLog, getLibraryAuditLog } from '@/shared/helpers/api.ts';
    import { http } from '@/shared/httpWrapper.js';
    import { LibraryAuditLogEntryDto } from '@/shared/helpers/api/types/entities.ts';
    import { longDate } from '@/formatters';
    import debounce from 'lodash/debounce';
    import { getLibraryLink } from '@/shared/helpers/routing.js';

    const props = defineProps({
        libraryName: {
            type: String,
            required: false,
            default: ''
        }
    });

    type EntityType = {
        entityType: string;
        isChecked: boolean;
    };

    type SearchFilter = {
        query: string;
        entityTypes: Set<string>;
    };

    const libraryName = toRef(props, 'libraryName');
    const changelog = ref<LibraryAuditLogEntryDto[]>([]);
    const entityTypes = ref<EntityType[]>([]);
    const searchQuery = ref<string>('');
    const filter = ref<SearchFilter>({
        query: '',
        entityTypes: new Set<string>()
    });

    const filteredChangelog = computed(() => {
        const query = filter.value.query.toLowerCase();
        const library = libraryName.value.toLowerCase();

        if (!query && filter.value.entityTypes.size === entityTypes.value.length) {
            return changelog.value;
        } else if (filter.value.entityTypes.size === 0) {
            return [];
        }

        return changelog.value.filter((item) => {
            const matchesQuery = !query
                || item.description?.toLowerCase().includes(query)
                || item.propertyChanges.some((x) => x.property.toLowerCase().includes(query));

            const matchesLibrary = !library || item.libraryName.toLowerCase().includes(library);

            const matchesEntityType = filter.value.entityTypes.has(item.entityType);

            return matchesQuery && matchesLibrary && matchesEntityType;
        });
    });

    // Watch for changes in searchQuery and update the filter
    watch(
        searchQuery,
        debounce((newValue: string) => filter.value.query = newValue, 300));

    // Watch for changes in entityTypes and update the filter
    watch(
        entityTypes,
        debounce((newValue: EntityType[]) => {
            newValue.forEach(({ entityType, isChecked }) => {
                if (isChecked)
                    filter.value.entityTypes.add(entityType);
                else
                    filter.value.entityTypes.delete(entityType);
            });
        }, 300),
        { deep: true }
    );

    onMounted(async () => {
        changelog.value = props.libraryName
            ? await getLibraryAuditLog(http, props.libraryName)
            : await getLibrariesAuditLog(http);

        const distinctEntityTypes = new Set(changelog.value.map((entry) => entry.entityType).sort());
        entityTypes.value = [...distinctEntityTypes].map((x) => ({
            entityType: x,
            isChecked: true
        }));
    });

    const selectAll = () => {
        entityTypes.value.forEach((x) => x.isChecked = true);
    };
    const clearSelected = () => {
        entityTypes.value.forEach((x) => x.isChecked = false);
    };
</script>

<template>
    <div>
        <div class="toolbar is-flex is-gap-10 mb-3">
            <b-dropdown
                class="mr-3"
                aria-role="list">
                <template #trigger>
                    <b-button
                        label="Filter by type"
                        type="is-info"
                        icon-right="menu-down" />
                </template>

                <b-dropdown-item
                    custom
                    class="is-flex is-flex-direction-row ">
                    <b-button
                        class="mr-3"
                        @click="clearSelected">
                        Clear selected
                    </b-button>

                    <b-button @click="selectAll">
                        Select all
                    </b-button>
                </b-dropdown-item>

                <b-dropdown-item separator />

                <b-dropdown-item
                    v-for="option in entityTypes"
                    :key="option.entityType"
                    custom
                    position="is-centered">
                    <p class="control">
                        <b-checkbox
                            v-model="option.isChecked">
                            {{ option.entityType }}
                        </b-checkbox>
                    </p>
                </b-dropdown-item>
            </b-dropdown>

            <b-input
                v-model="searchQuery"
                class="is-flex-grow-1"
                type="text"
                placeholder="Type to search in names, descriptions or names of changed properties..." />
        </div>
        <b-table
            :data="filteredChangelog"
            :narrowed="true"
            :hoverable="true"
            class="site-sticky-header"
            detailed>
            <b-table-column
                v-slot="table"
                field="timestamp"
                label="Timestamp (UTC)"
                width="120px"
                sortable>
                {{ longDate(table.row.timestamp) }}
            </b-table-column>
            <b-table-column
                v-if="!libraryName"
                v-slot="table"
                field="libraryName"
                label="Library"
                sortable>
                <router-link :to="getLibraryLink(table.row.libraryName)">
                    {{ table.row.libraryName }}
                </router-link>
            </b-table-column>
            <b-table-column
                v-slot="table"
                field="description"
                label="Description"
                sortable>
                {{ table.row.description }}
            </b-table-column>
            <b-table-column
                v-slot="table"
                field="user"
                label="User"
                sortable>
                {{ table.row.user }}
            </b-table-column>
            <template #detail="tableDetail">
                <div class="content">
                    <b-table
                        :data="tableDetail.row.propertyChanges"
                        :narrowed="true"
                        :hoverable="true"
                        class="pl-5">
                        <b-table-column
                            v-slot="changeTable"
                            field="property"
                            width="30%"
                            label="Property name">
                            {{ changeTable.row.property }}
                        </b-table-column>
                        <b-table-column
                            v-slot="changeTable"
                            width="35%"
                            field="oldValue"
                            label="Old value">
                            {{ changeTable.row.oldValue ?? "{null}" }}
                        </b-table-column>
                        <b-table-column
                            v-slot="changeTable"
                            field="newValue"
                            width="35%"
                            label="New value">
                            {{ changeTable.row.newValue ?? "{null}" }}
                        </b-table-column>
                    </b-table>
                </div>
            </template>
        </b-table>
    </div>
</template>

<style scoped>

</style>
